import { baseApi } from './base.api';
import { ACCOUNTS_URL, API_VERSION } from '../constants/api-routes';

// Replies used per product this period.
export type UsageByProducts = Partial<
  Record<'coagent' | 'cobi' | 'cocustomer' | 'playground', { by_source?: number }>
>;

// The account's general plan (accounts endpoint): limits, prices and usage.
export interface GeneralPlan {
  request_limit?: number;
  credits_count?: number;
  extra_request_price?: number;
  cancel_at_period_end?: boolean;
  status?: string;
  current_period_end?: string;
  plan?: {
    id?: number;
    name?: string;
    request_limit?: number;
    plan_type?: string;
    price?: number;
    extra_request_price?: number;
  };
  custom_plan_settings?: { request_limit?: number; fixed_price?: number; usage_price?: number };
  meta_data?: {
    usage_by_products?: UsageByProducts;
  };
  // How many projects of each product the plan allows.
  max_projects_coagent?: number;
  max_projects_cocustomer?: number;
  max_projects_cobi?: number;
  [key: string]: unknown;
}

// The signed-in account (accounts endpoint).
export interface Account {
  id?: number | string;
  company_name: string;
  email: string;
  phone_number?: string;
  email_for_invoices?: string;
  // API version the account uses ('v1' or 'v2'), and whether v2 has been set up.
  version?: string;
  version_v2_setup?: boolean;
  stripe_subscription_end?: string;
  general_plan?: GeneralPlan;
  [key: string]: unknown;
}

export const accountsService = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAccounts: build.query<Account, { account?: number | string } | void>({
      query: (params?: { account?: number | string }) => ({
        url: `${API_VERSION.V1}/${ACCOUNTS_URL}`,
        params: {
          ...(params && params.account !== '' && { account: params.account }),
        },
      }),
      providesTags: ['Accounts'],
    }),
    updateAccounts: build.mutation({
      query: (body) => ({
        url: `${API_VERSION.V1}/${ACCOUNTS_URL}`,
        method: 'PUT',
        body: body,
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedAccount } = await queryFulfilled;
          dispatch(
            accountsService.util.updateQueryData('getAccounts', undefined, (draft) => {
              Object.assign(draft, updatedAccount);
            }),
          );
        } catch {}
      },
    }),
  }),
});

export const { useGetAccountsQuery, useUpdateAccountsMutation, useLazyGetAccountsQuery } =
  accountsService;
